import { Stack, useRouter } from 'expo-router';
import { TabList, TabSlot, Tabs, TabTrigger } from 'expo-router/ui';
import { useRef } from 'react';
import { type ScrollView, StyleSheet } from 'react-native';
import { useQuery, useSubscription } from 'urql';
import * as Header from '@/components/Custom/Header';
import { TeamNavigationFragment } from '@/components/Navigation/ContextNavigationFragments';
import Text from '@/components/UI/Text';
import { graphql, readFragment } from '@/gql/graphql';
import Avatar from '../../components/Custom/Avatar';
import Nav from '../../components/Custom/Nav';
import * as NavItem from '../../components/Custom/NavItem';
import Box from '../../components/UI/Box';
import Icon from '../../components/Unistyles/Icon';
import { TeamDetailToolbarFragment } from './TeamDetailToolbarFragment';

const GetTeamDetail = graphql(`
  query GetTeamDetail($teamId: uuid!) {
    team_by_pk(id: $teamId) {
      ...TeamDetailToolbar
    }
  }
`, [TeamDetailToolbarFragment]);

const WatchTeamDetail = graphql(`
  subscription WatchTeamDetail($teamId: uuid!) {
    team_by_pk(id: $teamId) {
      ...TeamDetailToolbar
    }
  }
`, [TeamDetailToolbarFragment]);

const style = StyleSheet.create({
  tabSlot: {
    flex: 1,
  },
});

export type TeamDetailBasePath =
  | '/chat/team/[teamId]'
  | '/event/team/[teamId]'
  | '/organization/event/team/[teamId]'
  | '/organization/team/[teamId]'
  | '/team';

export type TeamDetailBadgesPathname =
  | '/chat/team/[teamId]/badge'
  | '/event/team/[teamId]/badge'
  | '/organization/event/team/[teamId]/badge'
  | '/organization/team/[teamId]/badge'
  | '/team/badges';

export default function TeamDetailLayout({
  basePath,
  badgesPathname,
  teamId,
}: {
  basePath: TeamDetailBasePath;
  badgesPathname: TeamDetailBadgesPathname;
  teamId: string | null;
}) {
  const { navigate } = useRouter();

  const [{ data }] = useQuery({
    query: GetTeamDetail,
    variables: { teamId: teamId || '' },
    pause: !teamId,
  });

  useSubscription({
    query: WatchTeamDetail,
    variables: { teamId: teamId || '' },
    pause: !teamId,
  });

  const toolbar = readFragment(TeamDetailToolbarFragment, data?.team_by_pk);
  const team = toolbar?.id === teamId ? toolbar : undefined;
  const navigation = readFragment(TeamNavigationFragment, team);
  const navRef = useRef<ScrollView>(null);
  const chatId = team?.chats?.[0]?.id;

  const actions = {
    edit: navigation?.permissions?.edit,
    delete: navigation?.permissions?.delete,
    leave: team?.on_team && navigation?.permissions?.delete_self,
    message: navigation?.permissions?.create_message && !!chatId,
  };

  const showActions = Object.values(actions).some(Boolean);

  const tabTriggers = [
    {
      name: 'members',
      shouldRender: true,
      label: 'People',
    },
    {
      name: 'badges',
      shouldRender: !!navigation?.permissions?.see_badge,
      label: 'Badges',
    },
  ];

  const visibleTabTriggers = tabTriggers.filter(
    (trigger) => trigger.shouldRender,
  );

  const headerTitle = () => {
    return (
      <Header.TitleContainer>
        <Avatar
          size="md"
          name={team?.title}
          id={team?.avatar_id ?? ''}
          color={team?.home_primary_color}
        />
        <Box flex>
          <Text
            numberOfLines={1}
            flexShrink={1}
            size="xl"
            testID="TeamDetailHeaderTitle"
          >
            {team?.title}
          </Text>
          {team && (team.root_event || navigation?.root_event_id === null) ? (
            <Box flex direction="horizontal" alignItems="center" space="sm">
              <Icon
                variant="typographySecondary"
                name={team?.root_event?.id ? 'globe-outline' : 'people-outline'}
                size="sm"
              />
              <Text
                variant="typographySecondary"
                size="xs"
                testID="TeamDetailHeaderOrganization"
              >
                {team.root_event ? team.root_event.title : 'Individual Team'}
              </Text>
            </Box>
          ) : null}
        </Box>
      </Header.TitleContainer>
    );
  };

  const headerRight = () =>
    showActions ? (
      <Header.End>
        <Header.ButtonGroup>
          {actions.message ? (
            <Header.Button
              accessibilityLabel="Open team chat"
              testID="TeamDetailChatButton"
              onPress={() => {
                navigate(
                  {
                    pathname: '/chat/[chatId]',
                    params: {
                      chatId,
                    },
                  },
                  { withAnchor: true },
                );
              }}
            >
              <Icon name="chatbubble-outline" size="2xl" />
            </Header.Button>
          ) : null}
          {actions.edit || actions.leave ? (
            <Header.Button
              accessibilityLabel="Team settings"
              testID="TeamDetailSettingsButton"
              onPress={() => {
                navigate({
                  pathname: '/team/[teamId]/edit',
                  params: {
                    teamId,
                  },
                });
              }}
            >
              <Icon name="settings-outline" size="2xl" />
            </Header.Button>
          ) : null}
        </Header.ButtonGroup>
      </Header.End>
    ) : null;

  return (
    <>
      <Stack.Screen
        options={{
          headerTitle,
          headerRight,
        }}
      />

      <Tabs testID="TeamDetailTabs">
        <Nav
          ref={navRef}
          data={visibleTabTriggers}
          keyExtractor={(item) => item.name}
          renderItem={({ item, index }) => (
            <TabTrigger resetOnFocus name={item.name} asChild>
              <NavItem.Container index={index}>
                <NavItem.Label>{item.label}</NavItem.Label>
              </NavItem.Container>
            </TabTrigger>
          )}
        />
        <TabList>
          <TabTrigger
            name="members"
            href={{
              pathname: basePath,
              params: { teamId },
            }}
          />
          <TabTrigger
            name="badges"
            href={{
              pathname: badgesPathname,
              params: { teamId },
            }}
          />
        </TabList>
        <TabSlot key={teamId} style={style.tabSlot} />
      </Tabs>
    </>
  );
}
